import { Text, Flex, Button, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { Lightning, PencilSimple, Trash } from '@phosphor-icons/react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { Schedule, ScheduleRun, DerivedScheduleStatus } from './types.js';
import {
  deriveScheduleStatus,
  derivedStatusVariants,
  runStatusVariants,
  formatRelative,
  formatDateTime,
  formatDuration,
} from './types.js';

type ScheduleActions = {
  onRunNow: (id: string) => void;
  onEdit: (schedule: Schedule) => void;
  onDelete: (id: string) => void;
  isRunning: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
};

export function getScheduleColumns(actions: ScheduleActions): ColumnDef<StockFeatures, Schedule>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <Text weight="medium">{row.original.name}</Text>,
    },
    {
      accessorKey: 'jobType',
      header: 'Job Type',
      cell: ({ row }) => <Text size="sm">{row.original.jobType}</Text>,
    },
    {
      accessorKey: 'scheduleType',
      header: 'Type',
      cell: ({ row }) => <TagBadge name={row.original.scheduleType} color="gray" hideDot />,
    },
    {
      id: 'status',
      // accessorFn (not accessorKey) since there's no persisted status column —
      // this also makes the derived status text matchable by the search filter.
      accessorFn: (row) => deriveScheduleStatus(row),
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue<DerivedScheduleStatus>();
        return (
          <TagBadge
            name={status}
            color={badgeVariantToTagColor(derivedStatusVariants[status])}
            hideDot
          />
        );
      },
    },
    {
      accessorKey: 'totalRuns',
      header: 'Runs',
      cell: ({ row }) => (
        <Text size="sm">
          {row.original.totalSuccesses}/{row.original.totalRuns}
        </Text>
      ),
    },
    {
      accessorKey: 'nextRunAt',
      header: 'Next Run',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {formatRelative(row.original.nextRunAt)}
        </Text>
      ),
    },
    {
      accessorKey: 'lastRunAt',
      header: 'Last Run',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {formatRelative(row.original.lastRunAt)}
        </Text>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Flex gap="xs" wrap="wrap">
          <Button
            variant="ghost"
            size="sm"
            onPress={() => actions.onRunNow(row.original.id)}
            isDisabled={actions.isRunning || !row.original.enabled}
            aria-label="Run now"
          >
            <Lightning size={16} weight="regular" />
            <Text size="xs" className="ml-1">
              Run
            </Text>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onPress={() => actions.onEdit(row.original)}
            isDisabled={actions.isUpdating}
            aria-label="Edit schedule"
          >
            <PencilSimple size={16} weight="regular" />
            <Text size="xs" className="ml-1">
              Edit
            </Text>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onPress={() => actions.onDelete(row.original.id)}
            isDisabled={actions.isDeleting}
            aria-label="Delete schedule"
          >
            <Trash size={16} weight="regular" />
            <Text size="xs" className="ml-1">
              Delete
            </Text>
          </Button>
        </Flex>
      ),
    },
  ];
}

export const runColumns: ColumnDef<StockFeatures, ScheduleRun>[] = [
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={row.original.status}
        color={badgeVariantToTagColor(runStatusVariants[row.original.status])}
        hideDot
      />
    ),
  },
  {
    accessorKey: 'scheduledFor',
    header: 'Scheduled For',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {formatDateTime(row.original.scheduledFor)}
      </Text>
    ),
  },
  {
    accessorKey: 'startedAt',
    header: 'Started',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {formatDateTime(row.original.startedAt)}
      </Text>
    ),
  },
  {
    accessorKey: 'durationMs',
    header: 'Duration',
    cell: ({ row }) => <Text size="sm">{formatDuration(row.original.durationMs)}</Text>,
  },
  {
    accessorKey: 'errorMessage',
    header: 'Error',
    cell: ({ row }) =>
      row.original.errorMessage ? (
        <Text
          size="sm"
          className="text-destructive truncate max-w-xs"
          title={row.original.errorMessage}
        >
          {row.original.errorMessage}
        </Text>
      ) : (
        <Text size="sm" className="text-muted-foreground">
          {'\u2014'}
        </Text>
      ),
  },
];
